import {
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import { Route as rootRoute } from "./__root";

// Auth Layout Route
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth-layout",
  component: lazyRouteComponent(() => import("layouts/auth")),
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/login",
  // Inverse of the dashboard guard: `authorized` is only true once the server
  // has validated the session, so a signed-in user has no business here.
  beforeLoad: ({ context }) => {
    if (context.authorized) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: lazyRouteComponent(() => import("pages/auth/login")),
});

// Landing page for the emailed staff verification link. Public: the token in
// the query string is the only credential, so this must sit outside the
// authorised layout.
const verifyEmailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/verify-email",
  component: lazyRouteComponent(() => import("pages/auth/verify_email")),
});

// Public: mail a code, set a new password. Also the way back in when the 90-day
// password limit expires, so it must sit outside the authorised layout.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/forgot-password",
  component: lazyRouteComponent(() => import("pages/auth/forgot_password")),
});

// Landing page for the IMS single sign-on redirect: the backend hands the
// session token over in the URL fragment. No beforeLoad guard here — it must not
// bounce before the token has been adopted.
const ssoRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/sso",
  component: lazyRouteComponent(() => import("pages/auth/sso")),
});

// Dashboard Layout Route
const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "dashboard-layout",
  beforeLoad: ({ context }) => {
    if (!context.authorized) {
      throw redirect({ to: "/auth/login" });
    }
  },
  component: lazyRouteComponent(() => import("layouts/dashboard")),
});

// Dashboard Index Route
const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/dashboard",
  component: lazyRouteComponent(() => import("pages/dashboard")),
});

// Settings Routes
const paymentTypesRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/payment_type",
  component: lazyRouteComponent(() => import("pages/settings/payment_type")),
});

const paymentDiscountRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/payment_discount",
  component: lazyRouteComponent(
    () => import("pages/settings/payment_discount"),
  ),
});

const notificationsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/notification",
  component: lazyRouteComponent(() => import("pages/settings/notification")),
});

const langsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/lang",
  component: lazyRouteComponent(() => import("pages/settings/lang")),
});

const pricePolicyRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/price_policy",
  component: lazyRouteComponent(
    () => import("pages/settings/price_policy"),
  ),
});

const pricePolicyHistoryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/price_policy/history",
  component: lazyRouteComponent(
    () => import("pages/settings/price_policy/history"),
  ),
});

const pricePolicyDetailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings/price_policy/$id",
  component: lazyRouteComponent(
    () => import("pages/settings/price_policy/$id"),
  ),
});

// Finance Routes
const orderRequestRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/order_request",
  component: lazyRouteComponent(() => import("pages/finance/order_request")),
});

const resolveOrderRequestRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/resolve-order-request",
  component: lazyRouteComponent(
    () => import("pages/finance/resolve-order-request"),
  ),
});

const tripReportByStaffRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/trip_report_by_staff",
  component: lazyRouteComponent(
    () => import("pages/finance/trip_report_by_staff"),
  ),
});

const insightsOverviewRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/insights/overview",
  component: lazyRouteComponent(() => import("pages/insights/overview")),
});

const insightsMapRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/insights/map",
  component: lazyRouteComponent(() => import("pages/insights/map")),
});

const insightsDailyRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/insights/daily",
  component: lazyRouteComponent(() => import("pages/insights/daily")),
});

const insightsMovementsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/insights/movements",
  component: lazyRouteComponent(() => import("pages/insights/movements")),
});

const insightsSalesRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/insights/sales",
  component: lazyRouteComponent(() => import("pages/insights/sales")),
});

const insightsInventoryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/insights/inventory",
  component: lazyRouteComponent(() => import("pages/insights/inventory")),
});

const inventoryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/inventory",
  component: lazyRouteComponent(() => import("pages/finance/inventory")),
});

const staffChangeRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/staff_change",
  component: lazyRouteComponent(() => import("pages/finance/staff_change")),
});

const staffWarehouseRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/staff_warehouse",
  component: lazyRouteComponent(() => import("pages/finance/staff_warehouse")),
});

const bankCheckRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/bank_check",
  component: lazyRouteComponent(() => import("pages/finance/bank_check")),
});

const veritechRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/veritech",
  component: lazyRouteComponent(() => import("pages/finance/veritech")),
});

const veritechIntegrationRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/veritech_integration",
  component: lazyRouteComponent(
    () => import("pages/finance/veritech_integration"),
  ),
});

const veritechDayStatusRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/veritech_day_status",
  component: lazyRouteComponent(
    () => import("pages/finance/veritech_day_status"),
  ),
});

const veritechIntegrationViewRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/veritech_integration/view/$id",
  component: lazyRouteComponent(
    () => import("pages/finance/veritech_integration/view"),
  ),
});

const veritechIntegrationViewRequestRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/veritech_integration/view-request/$id",
  component: lazyRouteComponent(
    () => import("pages/finance/veritech_integration/view_request"),
  ),
});

const invoiceMonthRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/invoice_month",
  component: lazyRouteComponent(() => import("pages/finance/invoice_month")),
});

const invoiceRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/finance/invoice",
  component: lazyRouteComponent(() => import("pages/finance/invoice")),
});

// Marketing Routes
const promoRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/marketing/promo",
  component: lazyRouteComponent(() => import("pages/marketing/promo")),
});

const advertisementRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/marketing/advertisement",
  component: lazyRouteComponent(() => import("pages/marketing/advertisement")),
});

const advertisementSingleRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/marketing/advertisement/$id",
  component: lazyRouteComponent(
    () => import("pages/marketing/advertisement/id"),
  ),
});

const campaignRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/marketing/campaign",
  component: lazyRouteComponent(() => import("pages/marketing/campaign")),
});

const advertisementMaterialRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/marketing/advertisement_material",
  component: lazyRouteComponent(
    () => import("pages/marketing/advertisement_material"),
  ),
});

const advertisementMaterialSubmissionRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/marketing/advertisement_material_submission",
  component: lazyRouteComponent(
    () => import("pages/marketing/advertisement_material_submission"),
  ),
});

// Feedback Routes
const machineFeedbackRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/feedback/machine_feedback",
  component: lazyRouteComponent(
    () => import("pages/feedback/machine_feedback"),
  ),
});

const customerFeedbackRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/feedback/customer_feedback",
  component: lazyRouteComponent(
    () => import("pages/feedback/customer_feedback"),
  ),
});

const machineFeedbackRefRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/feedback/machine_feedback_ref",
  component: lazyRouteComponent(
    () => import("pages/feedback/machine_feedback_ref"),
  ),
});

// Core Routes
const locationRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/location",
  component: lazyRouteComponent(() => import("pages/core/location")),
});

const tripRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/trip",
  component: lazyRouteComponent(() => import("pages/core/trip")),
});

const keyRegistrationRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/key_registration",
  component: lazyRouteComponent(() => import("pages/core/key_registration")),
});

const modelRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/model",
  component: lazyRouteComponent(() => import("pages/core/model")),
});

const machineRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/machine",
  component: lazyRouteComponent(() => import("pages/core/machine")),
});

const machineSingleRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/machine/$id",
  component: lazyRouteComponent(() => import("pages/core/machine/id")),
});

// Moved out of core into the Smart Fridge module
const machineAiRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/machine_ai",
  component: lazyRouteComponent(() => import("pages/smart_fridge/machine_ai")),
});

// Backward-compatible redirect for the old /core/smartvm path
const smartvmRedirectRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/core/smartvm",
  beforeLoad: () => {
    throw redirect({ to: "/smart_fridge/machine_ai" });
  },
});

// Menu Routes
const categoryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/category",
  component: lazyRouteComponent(() => import("pages/menu/category")),
});

const subCategoryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/sub_category",
  component: lazyRouteComponent(() => import("pages/menu/sub_category")),
});

const supplierRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/supplier",
  component: lazyRouteComponent(() => import("pages/menu/supplier")),
});

const measurementRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/measurement",
  component: lazyRouteComponent(() => import("pages/menu/measurement")),
});

const productRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/product",
  component: lazyRouteComponent(() => import("pages/menu/product")),
});

const trainRequestListRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/train_request",
  component: lazyRouteComponent(
    () => import("pages/menu/train_request/trainreqlist"),
    "TrainRequestListWrapper",
  ),
});

const trainRequestByProductRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/train_request_by_product",
  component: lazyRouteComponent(() => import("pages/menu/train_request")),
});

const trainRequestCreateRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/train_request_by_product/create",
  component: lazyRouteComponent(
    () => import("pages/menu/train_request/create"),
  ),
});

const productChangePriceRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/product/change_price/$id",
  component: lazyRouteComponent(
    () => import("pages/menu/product/change_price"),
  ),
});

const aiLearningRequestRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/menu/ai_learning_request",
  component: lazyRouteComponent(
    () => import("pages/menu/ai_learning_request"),
  ),
});

// User Routes
const staffRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/user/staff",
  component: lazyRouteComponent(() => import("pages/user/staff")),
});

const organizationRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/user/organization",
  component: lazyRouteComponent(() => import("pages/user/organization")),
});

const authLogsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/user/auth_logs",
  component: lazyRouteComponent(() => import("pages/user/auth_logs")),
});

const activityLogsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/user/activity_logs",
  component: lazyRouteComponent(() => import("pages/user/activity_logs")),
});

// Organization Section Routes
const organizationSectionOrgRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/organization/organization",
  component: lazyRouteComponent(
    () => import("pages/organization/organization"),
  ),
});

const organizationSectionOrgDetailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/organization/organization/$id",
  component: lazyRouteComponent(
    () => import("pages/organization/organization/$id"),
  ),
});

// RBAC Routes
const permissionRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/rbac/permission",
  component: lazyRouteComponent(() => import("pages/rbac/permission")),
});

const groupRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/rbac/group",
  component: lazyRouteComponent(() => import("pages/rbac/group")),
});

const roleRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/rbac/role",
  component: lazyRouteComponent(() => import("pages/rbac/role")),
});


// Monitor Routes
const systemLogRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/monitor/system_log",
  component: lazyRouteComponent(() => import("pages/monitor/system_log")),
});

const actionLogRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/monitor/action_log",
  component: lazyRouteComponent(() => import("pages/monitor/action_log")),
});

const machineLogRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/monitor/machine_log",
  component: lazyRouteComponent(() => import("pages/monitor/machine_log")),
});

// Application Management Routes
const newsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/news",
  component: lazyRouteComponent(
    () => import("pages/application_management/news"),
  ),
});

const appPromoRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/promo",
  component: lazyRouteComponent(
    () => import("pages/application_management/promo"),
  ),
});

const customerProductsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/products",
  component: lazyRouteComponent(
    () => import("pages/application_management/product"),
  ),
});

const transactionRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/transaction",
  component: lazyRouteComponent(
    () => import("pages/application_management/transaction"),
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/customers",
  component: lazyRouteComponent(
    () => import("pages/application_management/customer"),
  ),
});

const pointManagementRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/point_management",
  component: lazyRouteComponent(
    () => import("pages/application_management/point_management"),
  ),
});

const couponRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_management/coupon",
  component: lazyRouteComponent(
    () => import("pages/application_management/coupon"),
  ),
});

// Application Settings Routes
const termOfServiceRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_settings/term_of_service",
  component: lazyRouteComponent(
    () => import("pages/application_settings/term_of_service"),
  ),
});

const wheelRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_settings/wheel",
  component: lazyRouteComponent(
    () => import("pages/application_settings/wheel"),
  ),
});

const surveyRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/application_settings/survey",
  component: lazyRouteComponent(
    () => import("pages/application_settings/survey"),
  ),
});

// File Management Route
const fileManagementRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/file_management",
  component: lazyRouteComponent(() => import("pages/file_manage")),
});

const productImagesRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/file_management/product_images",
  component: lazyRouteComponent(
    () => import("pages/file_manage/product_images"),
  ),
});

// Smart Fridge Routes
// Copied entries reuse the existing page components under a /smart_fridge/* path.
const smartFridgeAiLearningRequestRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/ai_learning_request",
  component: lazyRouteComponent(() => import("pages/menu/ai_learning_request")),
});

const smartFridgeTrainRequestByProductRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/train_request_by_product",
  component: lazyRouteComponent(() => import("pages/menu/train_request")),
});

const smartFridgeInvoiceRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/invoice",
  component: lazyRouteComponent(() => import("pages/smart_fridge/invoice")),
});

const smartFridgeOrderRequestRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/order_request",
  component: lazyRouteComponent(() => import("pages/finance/order_request")),
});

const smartFridgeDisputedTransactionRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/disputed_transaction",
  component: lazyRouteComponent(
    () => import("pages/finance/resolve-order-request"),
  ),
});

const smartFridgeDeliveryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/delivery",
  component: lazyRouteComponent(() => import("pages/smart_fridge/delivery")),
});

const smartFridgeReceivableRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/receivable",
  component: lazyRouteComponent(() => import("pages/smart_fridge/receivable")),
});

const smartFridgeSettlementRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/smart_fridge/settlement",
  component: lazyRouteComponent(() => import("pages/smart_fridge/settlement")),
});

const smartFridgeCommissionRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/organization/commission",
  component: lazyRouteComponent(() => import("pages/smart_fridge/commission")),
});

// Exception Routes
const notAuthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/403",
  component: lazyRouteComponent(() => import("pages/exceptions/NotAuthorized")),
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "*",
  component: lazyRouteComponent(() => import("pages/exceptions/NotFound")),
});

// Index Route (redirect)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    if (context.authorized) {
      throw redirect({ to: "/dashboard" });
    } else {
      throw redirect({ to: "/auth/login" });
    }
  },
});

// Route Tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  authLayoutRoute.addChildren([
    loginRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    ssoRoute,
  ]),
  dashboardLayoutRoute.addChildren([
    dashboardIndexRoute,
    // Settings
    paymentTypesRoute,
    paymentDiscountRoute,
    notificationsRoute,
    langsRoute,
    pricePolicyRoute,
    pricePolicyDetailRoute,
    pricePolicyHistoryRoute,
    // Finance
    orderRequestRoute,
    resolveOrderRequestRoute,
    tripReportByStaffRoute,
    insightsOverviewRoute,
    insightsDailyRoute,
    insightsMapRoute,
    insightsSalesRoute,
    insightsInventoryRoute,
    insightsMovementsRoute,
    inventoryRoute,
    staffChangeRoute,
    staffWarehouseRoute,
    bankCheckRoute,
    veritechRoute,
    veritechIntegrationRoute,
    veritechDayStatusRoute,
    veritechIntegrationViewRoute,
    veritechIntegrationViewRequestRoute,
    invoiceMonthRoute,
    invoiceRoute,
    // Marketing
    promoRoute,
    advertisementRoute,
    advertisementSingleRoute,
    campaignRoute,
    advertisementMaterialRoute,
    advertisementMaterialSubmissionRoute,
    // Feedback
    machineFeedbackRoute,
    customerFeedbackRoute,
    machineFeedbackRefRoute,
    // Core
    locationRoute,
    tripRoute,
    keyRegistrationRoute,
    modelRoute,
    machineRoute,
    machineSingleRoute,
    smartvmRedirectRoute,
    // Menu
    categoryRoute,
    subCategoryRoute,
    supplierRoute,
    measurementRoute,
    productRoute,
    aiLearningRequestRoute,
    trainRequestListRoute,
    trainRequestByProductRoute,
    trainRequestCreateRoute,
    productChangePriceRoute,
    // User
    staffRoute,
    organizationRoute,
    authLogsRoute,
    activityLogsRoute,
    organizationSectionOrgRoute,
    organizationSectionOrgDetailRoute,
    // RBAC
    permissionRoute,
    groupRoute,
    roleRoute,
    // Monitor
    systemLogRoute,
    actionLogRoute,
    machineLogRoute,
    // Application Management
    newsRoute,
    appPromoRoute,
    customerProductsRoute,
    transactionRoute,
    customersRoute,
    pointManagementRoute,
    couponRoute,
    // Application Settings
    termOfServiceRoute,
    wheelRoute,
    surveyRoute,
    // File Management
    fileManagementRoute,
    productImagesRoute,
    // Smart Fridge
    machineAiRoute,
    smartFridgeAiLearningRequestRoute,
    smartFridgeTrainRequestByProductRoute,
    smartFridgeInvoiceRoute,
    smartFridgeOrderRequestRoute,
    smartFridgeDisputedTransactionRoute,
    smartFridgeDeliveryRoute,
    smartFridgeReceivableRoute,
    smartFridgeSettlementRoute,
    smartFridgeCommissionRoute,
  ]),
  notAuthorizedRoute,
  notFoundRoute,
]);

// Create Router
export const router = createRouter({
  routeTree,
  context: {
    authorized: false,
  },
  defaultPreload: "intent",
});

// Type Registration
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
